import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Clock,
  Car,
  Receipt,
  BarChart3,
  Users,
  Timer,
  FileText,
  Calculator,
  Shield,
  Zap,
  Globe,
  Bell,
  Palette,
  FolderKanban,
  ArrowRight,
  Check
} from 'lucide-react';
import MarketingLayout from '@/components/MarketingLayout';

export default function FeaturesPage() {
  const { t } = useTranslation();

  const mainFeatures = [
    {
      icon: Timer,
      title: t('features.timer.title'),
      description: t('features.timer.description'),
      benefits: [
        t('features.timer.benefit1'),
        t('features.timer.benefit2'),
        t('features.timer.benefit3')
      ],
      color: 'from-cyan-500 to-teal-500'
    },
    {
      icon: Car,
      title: t('features.mileage.title'),
      description: t('features.mileage.description'),
      benefits: [
        t('features.mileage.benefit1'),
        t('features.mileage.benefit2'),
        t('features.mileage.benefit3')
      ],
      color: 'from-teal-500 to-emerald-500'
    },
    {
      icon: Receipt,
      title: t('features.invoicing.title'),
      description: t('features.invoicing.description'),
      benefits: [
        t('features.invoicing.benefit1'),
        t('features.invoicing.benefit2'),
        t('features.invoicing.benefit3')
      ],
      color: 'from-emerald-500 to-green-500'
    },
    {
      icon: Users,
      title: t('features.clients.title'),
      description: t('features.clients.description'),
      benefits: [
        t('features.clients.benefit1'),
        t('features.clients.benefit2'),
        t('features.clients.benefit3')
      ],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: FolderKanban,
      title: t('features.projects.title'),
      description: t('features.projects.description'),
      benefits: [
        t('features.projects.benefit1'),
        t('features.projects.benefit2'),
        t('features.projects.benefit3')
      ],
      color: 'from-indigo-500 to-blue-500'
    },
    {
      icon: BarChart3,
      title: t('features.reports.title'),
      description: t('features.reports.description'),
      benefits: [
        t('features.reports.benefit1'),
        t('features.reports.benefit2'),
        t('features.reports.benefit3')
      ],
      color: 'from-violet-500 to-indigo-500'
    }
  ];

  const additionalFeatures = [
    { icon: Calculator, label: t('features.extra.kmCalculator') },
    { icon: Globe, label: t('features.extra.multiLang') },
    { icon: Bell, label: t('features.extra.reminders') },
    { icon: Palette, label: t('features.extra.templates') },
    { icon: Shield, label: t('features.extra.secure') },
    { icon: Zap, label: t('features.extra.fast') }
  ];

  return (
    <MarketingLayout
      title={t('features.meta.title')}
      description={t('features.meta.description')}
      keywords="urenregistratie functies, kilometerregistratie features, facturatie software, zzp tools"
    >
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-20 pb-24">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t('features.hero.title')}
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            {t('features.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="space-y-24">
            {mainFeatures.map((feature, index) => (
              <div
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
              >
                {/* Content */}
                <div className="flex-1">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-4">
                    {feature.title}
                  </h2>
                  <p className="text-lg text-slate-600 mb-6">
                    {feature.description}
                  </p>
                  <ul className="space-y-3">
                    {feature.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-4 h-4 text-teal-600" />
                        </div>
                        <span className="text-slate-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Visual placeholder */}
                <div className="flex-1">
                  <div className={`aspect-video rounded-2xl bg-gradient-to-br ${feature.color} opacity-10`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
            {t('features.extra.title')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {additionalFeatures.map((feature, index) => (
              <Card key={index} className="border-0 bg-white text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/10 to-teal-500/10 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-6 h-6 text-cyan-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">{feature.label}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-cyan-500 to-teal-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            {t('features.cta.title')}
          </h2>
          <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
            {t('features.cta.subtitle')}
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-cyan-600 hover:bg-cyan-50 px-10 py-6 text-lg rounded-xl"
          >
            <Link to="/register">
              {t('features.cta.button')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </MarketingLayout>
  );
}
